import { Col, Row } from "antd";
import cuid from "cuid";
import { useFormik } from "formik";
import { get } from "lodash";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AiOutlineLink } from "react-icons/ai";
import {
  MdArrowBackIos,
  MdArrowForwardIos,
  MdFileUpload,
  MdOutlineBadge,
  MdOutlineDescription,
  MdOutlineHome,
  MdOutlineSchool,
  MdPayment,
} from "react-icons/md";
import GenerateLinkModal from "screens/Private/Broker/Projects/List/Subscriptions/GenerateLinkModal";
import Alert from "shared/components/Alert";
import Button from "shared/components/Button";
import Card from "shared/components/Card";
import {
  PPEGELControlSection,
  PPEGELDrawer,
  PPEGELProvider,
} from "shared/components/PPEGEL";
import Loading from "shared/components/Spin";
import { useLazyQuery, useMutation } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { StoreContext } from "shared/store";
import format from "string-template";
import {
  CREATE_ATTACHMENT,
  DELETE_ATTACHMENT,
} from "utils/api/graphql/mutations/attachment";
import { SEND_SUBSCRIPTION_ADDITIONAL_DOCUMENTS } from "utils/api/graphql/mutations/subscription";
import { SUBSCRIPTION as SUBSCRIPTION_QUERY } from "utils/api/graphql/queries/subscription";
import { TRACER } from "utils/api/graphql/queries/tracers";
import {
  ATTACHMENT_TYPES,
  BANK_ACCOUNT_OWNER_TYPE,
  JOB_LIST,
  RELATIONSHIP,
  SOCIO_PROFESSIONAL_CATEGORIES,
  SUBSCRIPTION_FORM_KEYS,
  SUBSCRIPTION_STATUSES,
  THEMES_PALETTE,
} from "utils/constants";
import { calculateAge } from "utils/helpers/date";
import { STEPPER_KEYS } from "../..";
import Progress from "../Progress";
import generateProofForm from "./Forms";
import validation from "./validation";

export const DOCUMENTS_TYPE = {
  cin: ATTACHMENT_TYPES.CIN,
  payerCin: ATTACHMENT_TYPES.PAYER_CIN,
  receiverCin: ATTACHMENT_TYPES.RECEIVER_CIN,
  paymentAccount: ATTACHMENT_TYPES.PAYMENT_ACCOUNT,
  refundAccount: ATTACHMENT_TYPES.REFUND_ACCOUNT,
  socialSecurityCertificate:
    ATTACHMENT_TYPES.HEALTH_INSURANCE_RIGHTS_CERTIFICATE,
  proofOfAddress: ATTACHMENT_TYPES.PROOF_OF_ADDRESS,
  kbis: ATTACHMENT_TYPES.KBIS,
  pensionFund: ATTACHMENT_TYPES.PENSION_FUND,
  attachmentOfCommunity: ATTACHMENT_TYPES.COMMUNITY_ATTACHMENT,
  spouseSocialSecurityCertificate:
    ATTACHMENT_TYPES.HEALTH_INSURANCE_RIGHTS_CERTIFICATE,
  territorialCollectivityAttestation:
    ATTACHMENT_TYPES.TERRITORIAL_COLLECTIVITY_ATTESTATION,
  cnraclIrcantecAffiliation: ATTACHMENT_TYPES.CNRACL_IRCANTEC_AFFILIATION,
  // Children attestations handled dynamically with key: childSocialSecurityCertificate_{userId}
  // Children school certificates handled dynamically with key: childSchoolCertificate_{userId}
};

const ICONS = {
  [SUBSCRIPTION_FORM_KEYS.PROOF.SOCIAL_SECURITY_CERTIFICATE]: (
    <MdOutlineDescription size={16} />
  ),
  [SUBSCRIPTION_FORM_KEYS.PROOF.ENROLLMENT_CERTIFICATE]: (
    <MdOutlineSchool size={16} />
  ),
  [SUBSCRIPTION_FORM_KEYS.PROOF.ID]: <MdOutlineBadge size={16} />,
  [SUBSCRIPTION_FORM_KEYS.PROOF.BANK_ACCOUNT]: <MdPayment size={16} />,
  [SUBSCRIPTION_FORM_KEYS.PROOF.PAYER_RIB]: <MdPayment size={16} />,
  [SUBSCRIPTION_FORM_KEYS.PROOF.RECEIVER_RIB]: <MdPayment size={16} />,
  [SUBSCRIPTION_FORM_KEYS.PROOF.PROOF_OF_ADDRESS]: <MdOutlineHome size={16} />,
  [SUBSCRIPTION_FORM_KEYS.PROOF.KBIS]: <MdFileUpload size={16} />,
  [SUBSCRIPTION_FORM_KEYS.PROOF.PENSION_FUND]: <MdFileUpload size={16} />,
  [SUBSCRIPTION_FORM_KEYS.PROOF.COMMUNITY_ATTACHMENT]: (
    <MdFileUpload size={16} />
  ),
  [SUBSCRIPTION_FORM_KEYS.PROOF.TERRITORIAL_COLLECTIVITY_ATTESTATION]: (
    <MdFileUpload size={16} />
  ),
  [SUBSCRIPTION_FORM_KEYS.PROOF.CNRACL_IRCANTEC_AFFILIATION]: (
    <MdFileUpload size={16} />
  ),
};

const Proof = ({
  subscription,
  progress,
  setPercent,
  disabled,
  initialValues,
  setActive,
  calculateProgressMean,
  isSubmitting,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });
  const { user } = useContext(StoreContext);
  const hasEfficialConfig = !!get(
    user,
    "organization.config.EFFICIAL_CLIENT_ID",
  );
  const [ppeGelBlocked, setPpeGelBlocked] = useState(false);
  const handlePpeGelBlockedChange = useCallback((blocked) => {
    setPpeGelBlocked(blocked);
  }, []);
  const [addAttachment, { loading }] = useMutation(CREATE_ATTACHMENT);
  const [getTracer] = useLazyQuery(TRACER);

  const [sendAdditionalDocument] = useMutation(
    SEND_SUBSCRIPTION_ADDITIONAL_DOCUMENTS,
  );
  const refetchQueries = useMemo(
    () => [
      {
        query: SUBSCRIPTION_QUERY,
        awaitRefetchQueries: true,
        variables: {
          where: { id: subscription.id },
        },
      },
    ],
    [subscription.id],
  );

  const [deleteAttachment] = useMutation(DELETE_ATTACHMENT, {
    refetchQueries,
  });

  const [open, setOpen] = useState({});

  const onOpenGenerateLink = (data) => setOpen(data);

  // Determine payment conditions based on subscription data
  const paymentConditions = useMemo(() => {
    const payment = get(subscription, "payment");

    if (!payment) {
      return {
        isPayerReceiver: true,
        payerIsAdherent: true,
        receiverIsAdherent: true,
        requiresSubscriberPaysButDifferentReceiver: false,
        requiresThirdPartyPayer: false,
        requiresPayerCin: false,
        requiresBothThirdParty: false,
        requiresTwoSeparateRibs: false,
        isPayerOrganization: false,
      };
    }

    // Check if payer and receiver are the same account
    const receiverId = get(payment, "receiver.id");
    const payerId = get(payment, "payer.id");
    const isPayerReceiver = receiverId === payerId;

    // Check if payer is the adherent (subscriber)
    const payerIsAdherent = get(payment, "payer.owner.isAdherent", true);

    // Check if receiver is the adherent (subscriber)
    const receiverIsAdherent = get(payment, "receiver.owner.isAdherent", true);

    // Check if payer is an organization (autre payeur + switch organization)
    const payerType = get(payment, "payer.owner.type");
    const isPayerOrganization =
      payerType === BANK_ACCOUNT_OWNER_TYPE.COMPANY && !payerIsAdherent;

    // Subscriber pays premiums but doesn't receive reimbursements
    const requiresSubscriberPaysButDifferentReceiver =
      payerIsAdherent && !receiverIsAdherent && !isPayerReceiver;

    // Third-party payer: Subscriber doesn't pay premiums (someone else pays)
    const requiresThirdPartyPayer = !payerIsAdherent;

    // Payer CIN required when third-party payer is a particular person (not an organization)
    const requiresPayerCin = !payerIsAdherent && !isPayerOrganization;

    // Subscriber doesn't pay AND doesn't receive
    const requiresBothThirdParty =
      !payerIsAdherent && !receiverIsAdherent && !isPayerReceiver;

    // Requires 2 separate RIBs when payer and receiver are different accounts
    const requiresTwoSeparateRibs = !isPayerReceiver;

    return {
      isPayerReceiver,
      payerIsAdherent,
      receiverIsAdherent,
      requiresSubscriberPaysButDifferentReceiver,
      requiresThirdPartyPayer,
      requiresPayerCin,
      requiresBothThirdParty,
      requiresTwoSeparateRibs,
      isPayerOrganization,
    };
  }, [subscription]);

  // Calculate if there are children over 20 years old
  const hasChildrenOver20 = useMemo(() => {
    const relatedPersons = get(
      subscription,
      "project.contact.relatedPersons",
      [],
    ).filter((_) => _);

    const children = relatedPersons.filter(
      ({ relationship }) => relationship === RELATIONSHIP.CHILD,
    );

    return children.some((child) => {
      const birthDate = get(child, "identity.user.birthDate");
      return (
        calculateAge(
          birthDate,
          get(subscription, "project.contract.issuanceDate"),
        ) > 20
      );
    });
  }, [subscription]);

  // Determine professional situation conditions based on subscription data
  const professionalSituationConditions = useMemo(() => {
    const professionalSituation = get(
      subscription,
      "project.contact.fields.professionalSituation",
    );
    const profession = get(subscription, "project.contact.profession");

    // Check if profession is EXECUTIVES_AND_PUBLIC_SERVICE_EMPLOYEES (required for CA4 and CA5 documents)
    const isPublicServiceEmployee =
      profession ===
      SOCIO_PROFESSIONAL_CATEGORIES.EXECUTIVES_AND_PUBLIC_SERVICE_EMPLOYEES;

    if (!professionalSituation || !isPublicServiceEmployee) {
      return {
        professionalSituation: professionalSituation || null,
        profession: profession || null,
        isPublicServiceEmployee,
        isActive: false, // "En activité" - requires territorial collectivity attestation
        isRetired: false, // "Retraité" - requires CNRACL/IRCANTEC affiliation
        requiresTerritorialCollectivityAttestation: false,
        requiresCnraclIrcantecAffiliation: false,
      };
    }

    // Check professional situation type
    const isActive = professionalSituation === JOB_LIST.ACTIVE;
    const isRetired = professionalSituation === JOB_LIST.RETIRED;

    return {
      professionalSituation,
      profession,
      isPublicServiceEmployee,
      isActive,
      isRetired,
      // CA4: Attestation de la collectivité territoriale - Required when "En activité" AND profession is public service
      requiresTerritorialCollectivityAttestation:
        isActive && isPublicServiceEmployee,
      // CA5: Justificatif d'affiliation CNRACL ou IRCANTEC - Required when "Retraité" AND profession is public service
      requiresCnraclIrcantecAffiliation: isRetired && isPublicServiceEmployee,
    };
  }, [subscription]);

  useEffect(() => {
    calculateProgressMean(STEPPER_KEYS.PROOF, values);
  }, [
    get(subscription, "project.contact.fields.teletransmission"),
    get(subscription, "project.contact.socioProfessionalCategory"),
  ]);

  // Clean up attachments when conditions change
  const cleanupObsoleteAttachments = useCallback(async () => {
    const attachments = get(subscription, "attachments", []);
    const {
      requiresTwoSeparateRibs,
      requiresThirdPartyPayer,
      requiresBothThirdParty,
      requiresSubscriberPaysButDifferentReceiver,
    } = paymentConditions;
    const {
      requiresTerritorialCollectivityAttestation,
      requiresCnraclIrcantecAffiliation,
    } = professionalSituationConditions;

    // Get related persons
    const relatedPersons = get(
      subscription,
      "project.contact.relatedPersons",
      [],
    );
    const hasSpouse = relatedPersons.some(
      (p) => p?.relationship === RELATIONSHIP.SPOUSE,
    );
    const children = relatedPersons.filter(
      (p) => p?.relationship === RELATIONSHIP.CHILD,
    );
    const childUserIds = children.map((c) => get(c, "identity.user.id"));
    const spouseUserId = get(
      relatedPersons.find((p) => p?.relationship === RELATIONSHIP.SPOUSE),
      "identity.user.id",
    );

    const attachmentsToDelete = [];

    // If two separate RIBs are NOT required, delete refund account (paymentAccount is kept)
    if (!requiresTwoSeparateRibs) {
      attachments
        .filter((att) => att.type === ATTACHMENT_TYPES.REFUND_ACCOUNT)
        .forEach((att) => attachmentsToDelete.push(att.id));
    }

    // If third party payer is NOT required, delete payer CIN
    if (!requiresThirdPartyPayer && !requiresBothThirdParty) {
      attachments
        .filter((att) => att.type === ATTACHMENT_TYPES.PAYER_CIN)
        .forEach((att) => attachmentsToDelete.push(att.id));
    }

    // If receiver is not different from adherent, delete receiver CIN
    if (
      !requiresSubscriberPaysButDifferentReceiver &&
      !requiresBothThirdParty
    ) {
      attachments
        .filter((att) => att.type === ATTACHMENT_TYPES.RECEIVER_CIN)
        .forEach((att) => attachmentsToDelete.push(att.id));
    }

    // If no spouse, delete spouse attestation
    if (!hasSpouse) {
      attachments
        .filter(
          (att) =>
            att.type === ATTACHMENT_TYPES.HEALTH_INSURANCE_RIGHTS_CERTIFICATE &&
            (get(att, "user.id") || get(att, "userId")) === spouseUserId &&
            spouseUserId,
        )
        .forEach((att) => attachmentsToDelete.push(att.id));
    }

    // Delete children attestations for children that no longer exist
    attachments
      .filter((att) => {
        const attUserId = get(att, "user.id") || get(att, "userId");
        const adherentUserId = get(subscription, "project.contact.user.id");
        return (
          att.type === ATTACHMENT_TYPES.HEALTH_INSURANCE_RIGHTS_CERTIFICATE &&
          attUserId !== adherentUserId &&
          attUserId !== spouseUserId &&
          !childUserIds.includes(attUserId)
        );
      })
      .forEach((att) => attachmentsToDelete.push(att.id));

    // If territorial collectivity attestation is NOT required, delete it
    if (!requiresTerritorialCollectivityAttestation) {
      attachments
        .filter(
          (att) =>
            att.type === ATTACHMENT_TYPES.TERRITORIAL_COLLECTIVITY_ATTESTATION,
        )
        .forEach((att) => attachmentsToDelete.push(att.id));
    }

    // If CNRACL/IRCANTEC affiliation is NOT required, delete it
    if (!requiresCnraclIrcantecAffiliation) {
      attachments
        .filter(
          (att) => att.type === ATTACHMENT_TYPES.CNRACL_IRCANTEC_AFFILIATION,
        )
        .forEach((att) => attachmentsToDelete.push(att.id));
    }

    // Delete unique attachments
    const uniqueIds = [...new Set(attachmentsToDelete)];
    if (uniqueIds.length > 0) {
      await Promise.all(
        uniqueIds.map((id) =>
          deleteAttachment({
            variables: { where: { id } },
          }),
        ),
      );
    }
  }, [
    subscription,
    paymentConditions,
    professionalSituationConditions,
    deleteAttachment,
  ]);

  useEffect(() => {
    cleanupObsoleteAttachments();
  }, [cleanupObsoleteAttachments]);

  // Helper function to get the correct user ID based on document type
  const getUserIdForDocument = (key) => {
    // Adherent user ID
    const adherentUserId = get(subscription, "project.contact.user.id");

    // Spouse user ID
    const relatedPersons = get(
      subscription,
      "project.contact.relatedPersons",
      [],
    );
    const spouse = relatedPersons.find(
      (person) => person?.relationship === RELATIONSHIP.SPOUSE,
    );
    const spouseUserId = get(spouse, "identity.user.id");

    // Payer user ID (if different from adherent)
    const payerUserId = get(subscription, "payment.payer.owner.user.id");

    // Receiver user ID (if different from adherent)
    const receiverUserId = get(subscription, "payment.receiver.owner.user.id");

    // Children documents - extract user ID from key (childSocialSecurityCertificate_{userId})
    if (key.startsWith("childSocialSecurityCertificate_")) {
      const childUserId = key.replace("childSocialSecurityCertificate_", "");
      return childUserId;
    }

    // Children school certificates - extract user ID from key (childSchoolCertificate_{userId})
    if (key.startsWith("childSchoolCertificate_")) {
      const childUserId = key.replace("childSchoolCertificate_", "");
      return childUserId;
    }

    // Spouse documents
    if (key === "spouseSocialSecurityCertificate") {
      return spouseUserId;
    }

    // Payer documents
    if (key === "payerCin") {
      return payerUserId || adherentUserId;
    }

    // Receiver documents
    if (key === "receiverCin" || key === "refundAccount") {
      return receiverUserId || adherentUserId;
    }

    // Default to adherent for all other documents
    return adherentUserId;
  };

  const onSubmit = async (data) => {
    const documents = get(data, "proof", {});
    const documentsToUpload = [];
    Object.entries(documents)?.map(([key, attachments]) =>
      attachments
        ?.filter((e) => !e.status)
        .forEach((document) => {
          document.status = true;

          // Determine document type - for children social security, use HEALTH_INSURANCE_RIGHTS_CERTIFICATE
          // For children school certificates, use STUDENT_ID_OR_ENROLLMENT_CERTIFICATE
          let documentType = DOCUMENTS_TYPE[key];
          if (key.startsWith("childSocialSecurityCertificate_")) {
            documentType = ATTACHMENT_TYPES.HEALTH_INSURANCE_RIGHTS_CERTIFICATE;
          } else if (key.startsWith("childSchoolCertificate_")) {
            documentType =
              ATTACHMENT_TYPES.STUDENT_ID_OR_ENROLLMENT_CERTIFICATE;
          }

          // Get the appropriate user ID for this document
          const userId = getUserIdForDocument(key);

          documentsToUpload.push({
            id: cuid(),
            subscription: { id: get(subscription, "id") },
            ...(userId && { user: { id: userId } }),
            name: document.name,
            content: document.base64,
            contentType: document.contentType,
            type: documentType,
          });
        }),
    );
    const uploadedDocuments = await Promise.all(
      documentsToUpload.map((document, index) =>
        addAttachment({
          variables: {
            data: document,
          },
          refetchQueries:
            index === documentsToUpload?.length - 1 && refetchQueries,
        }),
      ),
    );

    // if (subscription.status === SUBSCRIPTION_STATUSES.SIGNED) {
    //   sendAdditionalDocument({
    //     variables: {
    //       where: { id: get(subscription, "id") },
    //       data: uploadedDocuments.map(({ data: { addAttachment } }) =>
    //         omitDeep(addAttachment, "__typename")
    //       ),
    //     },
    //   });
    // }

    calculateProgressMean(STEPPER_KEYS.PROOF, data);
    setActive(STEPPER_KEYS.DOCUMENT);
  };

  const { isValid, handleSubmit, setFieldValue, values, ...formProps } =
    useFormik({
      initialValues,
      onSubmit,
      validationSchema: validation(
        t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, {
          returnObjects: true,
        }),
      ),
    });

  const formList = useMemo(
    () =>
      Object.entries(
        generateProofForm({
          isRetired:
            (
              subscription?.insurancePlan?.config?.additionalDocuments || []
            ).includes("proof.pensionFund") &&
            subscription?.project?.contact?.socioProfessionalCategory ===
              SOCIO_PROFESSIONAL_CATEGORIES.RETIREES,
          isCommunityDocumentRequired:
            (
              subscription?.insurancePlan?.config?.additionalDocuments || []
            ).includes("proof.attachmentOfCommunity") &&
            subscription?.project?.contact?.socioProfessionalCategory !==
              SOCIO_PROFESSIONAL_CATEGORIES.RETIREES,
          isTNS: subscription?.insurancePlan?.fields?.tns,
          acceptTeletransmission:
            subscription?.project?.contact?.fields?.teletransmission ?? true,
          // Children over 20 condition
          hasChildrenOver20,
          // Payment conditions
          ...paymentConditions,
          // Professional situation conditions
          ...professionalSituationConditions,
        }),
      ).filter(([_, Form]) => Form),
    [
      subscription?.project?.contact?.socioProfessionalCategory,
      subscription?.insurancePlan?.fields?.tns,
      subscription?.project?.contact?.fields?.teletransmission,
      subscription?.insurancePlan?.config?.additionalDocuments,
      hasChildrenOver20,
      paymentConditions,
      professionalSituationConditions,
    ],
  );
  const onFileRemove = async ({ uid: id }) => {
    if (id.indexOf("AUTO") !== -1) return;
    return deleteAttachment({
      variables: {
        where: {
          id,
        },
      },
    });
  };

  return (
    <div key="proof" className="subscription-proof--wrapper">
      <GenerateLinkModal
        open={open}
        setOpen={setOpen}
        template={get(subscription, "project.fields.processTemplate")}
        tracerId={open?.tracerId}
      />

      <Row gutter={[16, 16]}>
        <Col lg={24} xs={24}>
          <Row align="middle" gutter={[16, 16]}>
            <Col lg={18} xs={24}>
              <Alert
                type="warning"
                showIcon
                message={SUBSCRIPTION.PROOF.WARNING}
              />
            </Col>
            <Col align="end" lg={6} xs={24}>
              <span className="indicator-step">
                {format(SUBSCRIPTION.INDICATOR, { count: "2", total: "3" })}
              </span>
            </Col>
          </Row>
        </Col>
        <Col lg={24} xs={24}>
          <Row gutter={[16, 16]}>
            <Col lg={9} xs={24}>
              <div className="card-skeleton--container highlighted">
                <div className="card-skeleton--wrapper">
                  <h3 className="title--md">{SUBSCRIPTION.PROOF.TITLE}</h3>
                  <div className="card-skeleton--wrapper__progress-list">
                    {formList.map(([form], index) => (
                      <Progress
                        key={"progress-" + index}
                        percent={progress["PROOF"][form]}
                        status={progress["PROOF"][form]}
                        translation={SUBSCRIPTION}
                        icon={ICONS[form]}
                        title={SUBSCRIPTION.PROOF.STEPS[form]}
                      />
                    ))}
                  </div>
                </div>
                <Button
                  type={"primary"}
                  size="full--width"
                  onClick={async () => {
                    const {
                      data: {
                        tracer: { source },
                      },
                    } = await getTracer({
                      variables: {
                        where: {
                          flow: {
                            process: {
                              theme:
                                THEMES_PALETTE[
                                  get(
                                    subscription,
                                    "project.fields.processTemplate",
                                  )
                                ].PROSPECT_PROCESS,
                            },
                          },
                        },
                      },
                    });
                    onOpenGenerateLink({
                      projectId: get(subscription, "project.id"),
                      tracerId: get(subscription, "project.tracer.id"),
                      subscriptionId: subscription.id,
                      insurancePlanIds: [get(subscription, "insurancePlan.id")],
                      isInfoCompleted: get(
                        subscription,
                        "additionalInfo.isInfoCompleted",
                      ),
                      isSigned:
                        get(subscription, "status") ===
                        SUBSCRIPTION_STATUSES.SIGNED,
                      baseUrl: get(
                        subscription,
                        "insurancePlan.config.generatedUrl",
                        `${process.env.REACT_APP_BASE_URL}/#/public/process/init/${source.id}`,
                      ),
                    });
                  }}
                  disabled
                >
                  {SUBSCRIPTION.GENERATE_LINK}
                  <AiOutlineLink size={24} />
                </Button>
              </div>
            </Col>
            {loading ? (
              <Loading />
            ) : (
              <Col lg={15} xs={24} gutter={16}>
                {hasEfficialConfig && (
                  <PPEGELProvider
                    projectId={get(subscription, "project.id")}
                    subscription={subscription}
                    onBlockedStatusChange={handlePpeGelBlockedChange}
                  >
                    <PPEGELControlSection />
                    <PPEGELDrawer />
                  </PPEGELProvider>
                )}
                <Card
                  title={SUBSCRIPTION.PROOF.TITLE}
                  subtitle={SUBSCRIPTION.PROOF.SUBTITLE}
                >
                  {formList.map(([key, Form], index) => {
                    return (
                      <Form
                        key={"form-" + index}
                        disabled={disabled}
                        subscription={subscription}
                        values={values}
                        formProps={{ ...formProps }}
                        setFieldValue={setFieldValue}
                        onFileRemove={onFileRemove}
                        setPercent={(data) => {
                          setPercent(STEPPER_KEYS.PROOF, key, data);
                        }}
                        paymentConditions={paymentConditions}
                      />
                    );
                  })}
                </Card>
                <div className="d-flex justify--between m-top-16">
                  <Button
                    type="secondary--link"
                    onClick={() =>
                      setActive(STEPPER_KEYS.ADDITIONAL_INFORMATION)
                    }
                  >
                    <MdArrowBackIos size={16} />
                    {SUBSCRIPTION.PROOF.ACTION.PREVIOUS}
                  </Button>

                  <Button
                    type="primary"
                    onClick={handleSubmit}
                    disabled={ppeGelBlocked}
                  >
                    {
                      SUBSCRIPTION.PROOF.ACTION.SAVE
                      // SUBSCRIPTION.PROOF.ACTION[
                      // subscription.status === SUBSCRIPTION_STATUSES.SIGNED
                      //   ? "SEND_DOCUMENTS"
                      //   : "SAVE"
                      // ]
                    }
                    <MdArrowForwardIos size={16} />
                  </Button>
                </div>
              </Col>
            )}
          </Row>
        </Col>
      </Row>
    </div>
  );
};

export default Proof;
